import {
  query,
  startAfter,
  limit,
  orderBy,
  getDocs,
  getCountFromServer,
  QueryFieldFilterConstraint,
  DocumentSnapshot
} from 'firebase/firestore'
import { ref, Ref, toValue, isRef } from 'vue'
import EntityService from '@/services/EntityService'

export type PaginatableServiceOptions = {
  numPerPage: number
  orderByField: string
  orderByDirection?: 'asc' | 'desc'
  where?: Ref<QueryFieldFilterConstraint[]> | QueryFieldFilterConstraint[]
}

/**
 * PaginatableService
 *
 * This class is a service that provides pagination functionality for Firestore queries.
 * see https://medium.com/@bdbdsameh00/pagination-in-firestore-487741f8162a
 *
 * Service for paginated Firestore queries using cursors (startAfter).
 * Supports forward and backward navigation via cursor history.
 */
export default class PaginatableService<T> {
  protected page: Ref<number> = ref(1)
  protected totalPages: Ref<number> = ref(1)
  protected orderByField: Ref<string> = ref('createdAt')
  protected orderByDirection: Ref<'asc' | 'desc'> = ref('asc')
  protected numPerPage: Ref<number> = ref(25)
  protected total: Ref<number> = ref(0)
  protected service: EntityService<T>
  protected where?: Ref<QueryFieldFilterConstraint[]> | QueryFieldFilterConstraint[]

  // Eng: Array of cursors (last doc per page), index = page - 1
  protected cursors: DocumentSnapshot[] = []

  constructor(service: EntityService<T>, options?: PaginatableServiceOptions) {
    this.service = service
    this.numPerPage.value = toValue(options?.numPerPage ?? 25)
    this.orderByField.value = toValue(options?.orderByField ?? 'createdAt')
    this.orderByDirection.value = options?.orderByDirection ?? 'asc'
    this.where = options?.where ?? []
  }

  /**
   * Loads the requested page
   * @param pageNumber page number (1-based)
   */
  public async fetchPage() {
    const collectionRef = await this.service.getCollectionGroup()
    const constraints = isRef(this.where) ? toValue(this.where) : this.where ?? []

    // Retrieve the total number of documents matching the filter
    const totalCount = (await getCountFromServer(query(collectionRef, ...constraints))).data().count
    this.total.value = totalCount
    this.totalPages.value = Math.max(1, Math.ceil(totalCount / this.numPerPage.value))

    // Base constraint with order and limit
    const queryConstraints: any[] = [
      ...constraints,
      orderBy(this.orderByField.value, this.orderByDirection.value),
      limit(this.numPerPage.value)
    ]

    // If page > 1, add startAfter with corresponding cursor
    if (this.page.value > 1) {
      const cursorIndex = this.page.value - 2
      if (this.cursors[cursorIndex]) {
        queryConstraints.push(startAfter(this.cursors[cursorIndex]))
      } else {
        // If cursor is missing, reset to page 1
        this.page.value = 1
      }
    }

    // Build the query with Firestore converter
    const queryRef = query(collectionRef, ...queryConstraints).withConverter(this.service.getConvertor())
    const snapshot = await getDocs(queryRef)

    // Retrieve the data
    const records = snapshot.docs.map(doc => doc.data()) as T[]

    // Store the cursor (last document of the page) for future navigation
    if (snapshot.docs.length > 0) {
      this.cursors[this.page.value - 1] = snapshot.docs[snapshot.docs.length - 1]
    }

    // Update the current page
    this.page.value = this.page.value

    return {
      data: records,
      total: this.total,
      page: this.page,
      perPage: this.numPerPage,
      totalPages: this.totalPages,
      lastDoc: this.cursors[this.page.value - 1] || null,
    }
  }

  /** Go to the next page if possible */
  public async nextPage() {
    if (this.page.value < this.totalPages.value) {
      this.page.value += 1
    }
    return null
  }

  /** Go to the previous page if possible */
  public async prevPage() {
    if (this.page.value > 1) {
      this.page.value -= 1
    }
    return null
  }

  /** Accessor for current page */
  get currentPage() {
    return this.page
  }

  /** Accessor for total page count */
  get totalPageCount() {
    return this.totalPages
  }

  /** Accessor for total records */
  get totalRecords() {
    return this.total
  }
}


export function usePaginatableService<T>(service: EntityService<T>, options?: PaginatableServiceOptions) {
  const paginatableService = new PaginatableService<T>(service, options)

  paginatableService.fetchPage()

  return {
    fetcher: paginatableService.fetchPage.bind(paginatableService),
    nextPage: paginatableService.nextPage.bind(paginatableService),
    prevPage: paginatableService.prevPage.bind(paginatableService),
    currentPage: paginatableService.currentPage,
    totalPageCount: paginatableService.totalPageCount,
    totalRecords: paginatableService.totalRecords,
  }
}
